import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { formatEuro, formatDateFr } from "@/lib/format";
import { CreditCard, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "@/components/common/StatusBadge";

const KINDS = { mortgage: "Prêt immobilier", works_loan: "Prêt travaux", acquisition_loan: "Prêt acquisition", revolving: "Crédit renouvelable", other: "Autre" };
const STATUSES = { active: "Actif", closed: "Soldé", refinanced: "Refinancé", archived: "Archivé", pending: "En cours", renegotiated: "Renégocié", defaulted: "Défaut" };

const EMPTY = {
  label: "", bank: "", initial_amount: "", start_date: "", monthly_payment: "",
  interest_rate: "", kind: "mortgage", status: "active", scope: "acquisition",
};

export default function BuildingFinancingsTab({ building }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/financings?building_id=${building.id}`);
      setItems(data.items || []);
    } catch (err) {
      console.error("Load financings failed:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [building.id]);

  const create = async () => {
    try {
      const payload = { ...form, building_ids: [building.id] };
      ["initial_amount", "monthly_payment", "interest_rate"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
        else payload[k] = parseFloat(payload[k]);
      });
      if (!payload.start_date) delete payload.start_date;
      await api.post("/financings", payload);
      toast.success("Financement enregistré"); setOpen(false); setForm({ ...EMPTY }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce financement ? (Il sera archivé s'il possède un historique.)")) return;
    try {
      await api.delete(`/financings/${id}`);
      toast.success("Supprimé"); load();
    } catch (err) {
      if (err.response?.status === 409) {
        // Auto-archive with user confirmation
        if (window.confirm(err.response.data.detail + "\n\nArchiver au lieu de supprimer ?")) {
          try { await api.delete(`/financings/${id}?archive=true`); toast.success("Archivé"); load(); }
          catch { toast.error("Archivage impossible"); }
        }
      } else { toast.error("Suppression impossible"); }
    }
  };

  const patchStatus = async (id, status) => {
    try { await api.patch(`/financings/${id}`, { status }); toast.success("Statut mis à jour"); load(); }
    catch { toast.error("Mise à jour impossible"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-overline">Rappel</div>
          <p className="text-sm text-muted-foreground max-w-xl">Le module Crédit détaillé (tableaux d&apos;amortissement, TAEG, garanties, avenants…) sera livré ultérieurement. Ici, l&apos;essentiel est journalisé pour préparer le rattachement.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-pill" data-testid="financing-add-btn"><Plus className="mr-2 h-4 w-4" /> Nouveau financement</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau financement</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label>Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="financing-form-label" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Banque</Label><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(KINDS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Montant initial</Label><Input type="number" value={form.initial_amount} onChange={(e) => setForm({ ...form, initial_amount: e.target.value })} data-testid="financing-form-amount" /></div>
                <div><Label>Mensualité</Label><Input type="number" step="0.01" value={form.monthly_payment} onChange={(e) => setForm({ ...form, monthly_payment: e.target.value })} /></div>
                <div><Label>Taux %</Label><Input type="number" step="0.01" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Date de début</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUSES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={create} data-testid="financing-form-submit">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {loading && <div className="text-sm text-muted-foreground py-4">Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="card-elevated p-12 text-center text-muted-foreground">
            <CreditCard className="mx-auto h-8 w-8 mb-3 text-muted-foreground/60" /> Aucun financement rattaché.
          </div>
        )}
        {items.map((f) => (
          <div key={f.id} className="card-elevated p-6" data-testid={`financing-row-${f.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-accent">{KINDS[f.kind] || f.kind}</div>
                <h3 className="mt-1 font-heading text-lg">{f.label}</h3>
                <div className="text-xs text-muted-foreground">{f.bank}</div>
                <div className="mt-2 flex gap-2">
                  <StatusBadge tone={f.status === "active" ? "success" : "neutral"}>{STATUSES[f.status]}</StatusBadge>
                  {f.start_date && <span className="text-xs text-muted-foreground">Début {formatDateFr(f.start_date)}</span>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-serif text-2xl">{formatEuro(f.initial_amount)}</div>
                <div className="text-xs text-muted-foreground">Mensualité <span className="font-mono">{formatEuro(f.monthly_payment, true)}</span></div>
                {f.interest_rate != null && <div className="text-xs text-muted-foreground">Taux {f.interest_rate} %</div>}
                <Button variant="ghost" size="sm" onClick={() => del(f.id)} className="mt-2"><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
